import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, Copy, Check, Share2, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * 친구에게 추천하기 모달
 * - 내 추천 링크(?ref=코드)를 복사/공유
 * - 친구가 가입하면 추천인에게 5,000P 지급
 */
export default function ReferralModal({ open, onClose, user }) {
  const [copied, setCopied] = useState(false);

  const code = user?.referral_code || "";
  const link = code ? `${window.location.origin}/?ref=${code}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("추천 링크를 복사했어요!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 클립보드 미지원 환경 폴백
      const el = document.createElement("textarea");
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      toast.success("추천 링크를 복사했어요!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = async () => {
    const shareData = {
      title: "공감놀이터 리즈카페",
      text: "중년들의 따뜻한 만남, 공감놀이터 리즈카페에 함께해요 🌿",
      url: link,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* 사용자 취소 */ }
    } else {
      copyLink();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="referral-modal"
        >
          <motion.div
            className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl"
            initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-stone-600 to-stone-500 px-6 pt-7 pb-8 text-center text-white">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white"
                data-testid="referral-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-14 h-14 mx-auto rounded-2xl bg-white/15 flex items-center justify-center mb-3">
                <Gift className="w-7 h-7" />
              </div>
              <h2 className="font-serif text-2xl font-bold">친구에게 추천하기</h2>
              <p className="text-sm text-white/85 mt-1.5 leading-relaxed">
                친구가 내 링크로 가입하면<br />
                <span className="font-bold text-white">나는 5,000P · 친구는 2,000P</span>를 받아요 🎁
              </p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-stone-500 mb-2">내 추천 링크</label>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={link}
                    className="flex-1 min-w-0 px-3 py-3 rounded-xl bg-stone-50 border border-stone-200 text-[13px] text-stone-700 outline-none truncate"
                    data-testid="referral-link-input"
                  />
                  <button
                    onClick={copyLink}
                    className="shrink-0 w-11 h-11 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-600 flex items-center justify-center transition-colors"
                    data-testid="referral-copy-btn"
                    aria-label="링크 복사"
                  >
                    {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
                {code && (
                  <p className="text-[12px] text-stone-400 mt-2">
                    추천 코드: <span className="font-bold text-stone-600">{code}</span>
                  </p>
                )}
              </div>

              <button
                onClick={shareLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-stone-700 hover:bg-stone-800 text-white font-semibold transition-colors"
                data-testid="referral-share-btn"
              >
                <Share2 className="w-4.5 h-4.5" />
                링크 공유하기
              </button>

              <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-stone-50 border border-stone-100">
                <Users className="w-4 h-4 text-stone-500 shrink-0" />
                <p className="text-[12.5px] text-stone-600">
                  지금까지 <span className="font-bold text-stone-800">{user?.referral_count || 0}명</span>을 초대했어요.
                </p>
              </div>

              <p className="text-[11px] text-stone-400 leading-relaxed text-center">
                친구가 링크로 접속해 회원가입을 완료하면 자동으로 포인트가 적립됩니다.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
